import React, { useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewAppSidebar } from './NewAppSidebar';
import { useAuth } from '@/hooks/useAuth';
import { useLayout, useShouldShowSidebar } from '@/contexts/LayoutContext';
import { toast } from '@/hooks/use-toast';

interface GlobalSidebarProps {
  onBuyCredits?: () => void;
}

/**
 * GlobalSidebar - Composant sidebar unique et centralisé avec optimisations de performance
 * 
 * Ce composant centralise toute la logique de la sidebar pour éliminer
 * la duplication de code sur toutes les pages. Il gère:
 * - La logique de navigation centralisée
 * - La gestion des hooks (useDashboard, useAuth)
 * - La logique de déconnexion avec gestion d'erreurs
 * - L'état des crédits en temps réel
 * - Memoization pour les performances
 * 
 * Exigences: 1.1, 1.3, 6.2, 5.1, 5.3
 */
const GlobalSidebarComponent: React.FC<GlobalSidebarProps> = ({ onBuyCredits }) => {
  const { signOut, user } = useAuth();
  const { currentLayout, sidebarState } = useLayout();
  const shouldShowSidebar = useShouldShowSidebar();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  

  
  // Logique centralisée pour la déconnexion avec gestion d'erreurs
  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return; // Éviter les doubles clics
    
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate("/");
      toast({
        title: "Déconnexion réussie",
        description: "Vous avez été déconnecté avec succès.",
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      toast({
        title: "Erreur de déconnexion",
        description: "Une erreur s'est produite lors de la déconnexion. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  }, [signOut, navigate, isLoggingOut]);
  
  // Logique centralisée pour la navigation avec validation
  const handleNavigate = useCallback((route: string) => {
    try {
      navigate(route);
    } catch (error) {
      console.error('Erreur de navigation:', error);
      toast({
        title: "Erreur de navigation",
        description: "Impossible de naviguer vers cette page.",
        variant: "destructive",
      });
    }
  }, [navigate]);
  
  // Gestion d'erreur si les hooks ne sont pas disponibles
  if (!user) {
    return null;
  }
  
  // Ne pas rendre la sidebar si elle ne doit pas être visible selon le layout
  if (!shouldShowSidebar) {
    return null;
  }
  
  return (
    <div 
      data-testid="global-sidebar" 
      className="global-sidebar-container"
      data-layout={currentLayout}
      data-sidebar-state={sidebarState}
    >
      <NewAppSidebar
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        onBuyCredits={onBuyCredits}
      />
    </div>
  );
};

// Memoization du composant pour éviter les re-renders inutiles
// Pas de fonction de comparaison custom car le composant n'a pas de props
// React utilisera la comparaison par défaut (shallow comparison)
export const GlobalSidebar = memo(GlobalSidebarComponent);